package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.domain.util.AppClock
import com.evoapps.evoshift.data.dto.staff.StaffHrDto
import com.evoapps.evoshift.data.dto.staff.StaffNotesDto
import com.evoapps.evoshift.data.dto.staff.StaffReadinessDto
import com.evoapps.evoshift.data.mapper.StaffMapper
import com.evoapps.evoshift.domain.model.StaffReadiness
import com.evoapps.evoshift.domain.model.StaffRecord
import com.evoapps.evoshift.domain.repository.StaffRepository
import com.evoapps.evoshift.data.remote.AppFirestore

/**
 * SH-47 staff fields in Firestore (see StaffChecks.kt for who reads what):
 * - users/{uid}/hr/{wpId}: the dates (the person, owners and financial managers);
 * - workplaces/{wpId}/readiness/{uid}: the statuses, written with the dates in one batch and
 *   refreshed daily by functions/staffReadiness.js (the workplace's managers, the person);
 * - workplaces/{wpId}/staff_notes/{uid}: availability notes (the managers, the person).
 */
class StaffRepositoryImpl(private val firestore: AppFirestore) : StaffRepository {

    private fun hr(userId: String, workplaceId: String) =
        firestore.collection("users").document(userId).collection("hr").document(workplaceId)

    private fun readinessOf(workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection("readiness")

    private fun notesOf(userId: String, workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection("staff_notes").document(userId)

    override suspend fun getStaffRecord(userId: String, workplaceId: String): Result<StaffRecord?> = runCatching {
        hr(userId, workplaceId).get().takeIf { it.exists }
            ?.let { StaffMapper.toDomain(it.data<StaffHrDto>(), userId, workplaceId) }
    }

    override suspend fun saveStaffRecord(record: StaffRecord, readiness: StaffReadiness): Result<Unit> = runCatching {
        val batch = firestore.batch()
        batch.set(hr(record.userId, record.workplaceId), StaffMapper.toDto(record))
        batch.set(readinessOf(record.workplaceId).document(record.userId), StaffMapper.toDto(readiness))
        batch.commit()
    }

    override suspend fun getReadiness(workplaceId: String): Result<List<StaffReadiness>> = runCatching {
        readinessOf(workplaceId).get().documents.map { StaffMapper.toDomain(it.data<StaffReadinessDto>(), it.id, workplaceId) }
    }

    override suspend fun getReadinessOf(userId: String, workplaceId: String): Result<StaffReadiness?> = runCatching {
        readinessOf(workplaceId).document(userId).get().takeIf { it.exists }
            ?.let { StaffMapper.toDomain(it.data<StaffReadinessDto>(), userId, workplaceId) }
    }

    override suspend fun getAvailabilityNotes(userId: String, workplaceId: String): Result<String?> = runCatching {
        notesOf(userId, workplaceId).get().takeIf { it.exists }?.data<StaffNotesDto>()?.notes?.takeIf { it.isNotBlank() }
    }

    override suspend fun saveAvailabilityNotes(userId: String, workplaceId: String, notes: String, editorId: String): Result<Unit> = runCatching {
        val ref = notesOf(userId, workplaceId)
        if (notes.isBlank()) {
            ref.delete()
        } else {
            ref.set(StaffNotesDto(userId = userId, notes = notes, updatedAt = AppClock.now().toString(), updatedBy = editorId))
        }
    }
}
